<template>
    <v-container>
      <!-- Header Section -->
      <v-row class="mb-4">
        <v-col cols="12">
          <h1 class="text-h4">Files & Attachments</h1>
          <p class="text-subtitle-1">Manage documents for {{ propertyName }}</p>
        </v-col>
      </v-row>
  
      <!-- Add File Button -->
      <v-row class="mb-4">
        <v-col cols="12" sm="12" md="12" class="d-flex">
          <div class="button-container">
            <button class="add-button" @click="showUploadDialog = true">Add File</button>
          </div>
        </v-col>
      </v-row>
  
      <!-- Files List Section -->
      <v-row>
        <v-col cols="12">
          <v-card outlined>
            <v-card-title>Attached Files</v-card-title>
            <v-card-text>
              <v-data-table
                :headers="fileHeaders"
                :items="attachments"
                :loading="loading"
                :items-per-page="10"
                class="elevation-1"
                :no-data-text="loading ? 'Loading...' : 'No files attached yet'"
              >
                <template v-slot:item.name="{ item }">
                  <a @click="downloadFile(item)" class="file-link">
                    <v-icon left>mdi-file</v-icon>
                    {{ item.name }}
                  </a>
                </template>
  
                <template v-slot:item.size="{ item }">
                  {{ formatFileSize(item.size) }}
                </template>
  
                <template v-slot:item.uploadedAt="{ item }">
                  {{ formatDate(item.uploadedAt) }}
                </template>
  
                <template v-slot:item.actions="{ item }">
                  <v-btn icon small @click="downloadFile(item)">
                    <v-icon small>mdi-download</v-icon>
                  </v-btn>
                  <v-btn icon small @click="confirmDelete(item)">
                    <v-icon small color="error">mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
  
      <!-- Upload File Dialog -->
      <v-dialog v-model="showUploadDialog" max-width="600">
        <v-card>
          <v-card-title class="headline">Upload New File</v-card-title>
          <v-card-text>
            <v-file-input
              v-model="newFile"
              label="Select file"
              prepend-icon="mdi-paperclip"
              outlined
              dense
              :rules="fileRules"
              accept="*"
              @change="onFileChange"
            ></v-file-input>
            <v-text-field
              v-model="fileDescription"
              label="Description (optional)"
              outlined
              dense
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showUploadDialog = false">Cancel</v-btn>
            <v-btn 
              color="primary" 
              :disabled="!newFile || uploading" 
              @click="uploadFile"
            >
              <v-icon left>mdi-upload</v-icon>
              Upload
              <v-progress-circular
                v-if="uploading"
                indeterminate
                size="24"
                class="ml-2"
              ></v-progress-circular>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">Confirm Delete</v-card-title>
          <v-card-text>
            Are you sure you want to delete "{{ fileToDelete?.name }}"?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="error" text @click="deleteFile">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </template>
  
  <script>
  import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    deleteDoc,
    doc,
    getFirestore,
  } from "firebase/firestore";
  import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
  import { getAuth } from "firebase/auth";
  
  export default {
    name: "Attachments",
    props: {
      item: Object,
      collectionName: String, // 'properties', 'tenants', 'leases', or 'landlords'
    },
    data() {
      return {
        propertyId: null,
        propertyName: "",
        newFile: null,
        fileDescription: "",
        uploading: false,
        loading: false,
        attachments: [],
        deleteDialog: false,
        fileToDelete: null,
        showUploadDialog: false,
        fileHeaders: [
          { text: "File Name", value: "name" },
          { text: "Description", value: "description" },
          { text: "Size", value: "size" },
          { text: "Uploaded By", value: "uploadedBy" },
          { text: "Date Uploaded", value: "uploadedAt" },
          { text: "Actions", value: "actions", sortable: false },
        ],
        fileRules: [
          (v) => !!v || "File is required",
          (v) => !v || v.size < 10000000 || "File size should be less than 10 MB",
        ],
      };
    },
    async created() {
      this.propertyId = this.$route.params.id || this.item?.id;
      this.propertyName = this.item?.property || this.item?.name || "Unknown";
      this.collectionName = this.$route.query.collection || "properties";
      await this.loadAttachments();
    },
    methods: {
      async loadAttachments() {
        this.loading = true;
        try {
          const db = getFirestore();
          const attachmentsRef = collection(
            db,
            this.collectionName,
            this.propertyId,
            "attachments"
          );
          const q = query(attachmentsRef);
          const querySnapshot = await getDocs(q);
  
          this.attachments = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        } catch (error) {
          console.error("Error loading attachments:", error);
          this.$toast.error("Failed to load attachments");
        } finally {
          this.loading = false;
        }
      },
      onFileChange(file) {
        if (file && file.size > 10000000) {
          this.$toast.warning("File size should be less than 10 MB");
          this.newFile = null;
        }
      },
      async uploadFile() {
        if (!this.newFile) return;
  
        this.uploading = true;
        try {
          const auth = getAuth();
          const user = auth.currentUser;
          const storage = getStorage();
          const db = getFirestore();
  
          // Create unique filename with timestamp
          const fileExtension = this.newFile.name.split(".").pop();
          const fileName = `${Date.now()}.${fileExtension}`;
          const storagePath = `${this.collectionName}/${this.propertyId}/attachments/${fileName}`;
          const storageRef = ref(storage, storagePath);
  
          // Upload file to Firebase Storage
          await uploadBytes(storageRef, this.newFile);
          const downloadURL = await getDownloadURL(storageRef);
  
          // Add file metadata to Firestore
          const attachmentData = {
            name: this.newFile.name,
            description: this.fileDescription,
            size: this.newFile.size,
            type: this.newFile.type,
            url: downloadURL,
            storagePath: storagePath,
            uploadedBy: user?.displayName || user?.email || "Anonymous",
            uploadedAt: new Date().toISOString(),
          };
  
          const attachmentsRef = collection(
            db,
            this.collectionName,
            this.propertyId,
            "attachments"
          );
          await addDoc(attachmentsRef, attachmentData);
  
          // Refresh attachments list
          await this.loadAttachments();
  
          // Reset form and close dialog
          this.newFile = null;
          this.fileDescription = "";
          this.showUploadDialog = false;
          this.$toast.success("File uploaded successfully");
        } catch (error) {
          console.error("Error uploading file:", error);
          this.$toast.error("Failed to upload file");
        } finally {
          this.uploading = false;
        }
      },
      downloadFile(file) {
        // For PDFs and images, open in new tab for preview
        if (file.type.includes("pdf") || file.type.includes("image")) {
          window.open(file.url, "_blank");
        } else {
          // For other files, trigger download
          const link = document.createElement("a");
          link.href = file.url;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      },
      confirmDelete(file) {
        this.fileToDelete = file;
        this.deleteDialog = true;
      },
      async deleteFile() {
        if (!this.fileToDelete) return;
  
        try {
          const db = getFirestore();
          const storage = getStorage();
  
          // Delete from Firestore
          await deleteDoc(
            doc(
              db,
              this.collectionName,
              this.propertyId,
              "attachments",
              this.fileToDelete.id
            )
          );
  
          // Delete from Storage
          const storageRef = ref(storage, this.fileToDelete.storagePath);
          await deleteObject(storageRef);
  
          // Refresh list
          await this.loadAttachments();
          this.$toast.success("File deleted successfully");
        } catch (error) {
          console.error("Error deleting file:", error);
          this.$toast.error("Failed to delete file");
        } finally {
          this.deleteDialog = false;
          this.fileToDelete = null;
        }
      },
      formatFileSize(bytes) {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
      },
      formatDate(dateString) {
        return new Date(dateString).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
  };
  </script>
  
  <style scoped>
  .file-link {
    color: #1976d2;
    text-decoration: none;
    cursor: pointer;
  }
  .file-link:hover {
    text-decoration: underline;
  }
  
  .button-container {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  
  .add-button {
    background-color: #F1C284;
    color: white;
    border: none;
    padding: 10px 20px;
    text-align: center;
    text-decoration: none;
    display: inline-block;
    font-size: 16px;
    margin: 4px 2px;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.3s;
  }
  
  .add-button:hover {
    background-color: #45a049;
  }
  </style>